import { CalendarClock, ChevronRight, ClipboardList, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '../../../components/Button';
import { Dialog } from '../../../components/Dialog';
import { cn } from '../../../lib/cn';
import { formatDateRO, formatDateTimeRO, pluralRO } from '../../../lib/format';
import { TicketDetailBody } from '../TicketDetailPage';
import { useListTicketsQuery } from '../tickets.api';
import { isOverdue, type Ticket, type TicketReferenceType } from '../tickets.schema';

import { TicketFormDialog, type PendingRef } from './TicketFormDialog';
import { TicketStatusBadge } from './TicketStatusBadge';

type EntityProps = {
    itemType: TicketReferenceType;
    /** The referenced object's id (order/user/product id). */
    itemId: string | number;
    /** Display snapshot stored on the reference (e.g. "Comanda #1234", product title). */
    label: string;
    /**
     * Also list archived tickets (full history). Only the order view sets
     * this — other entity panels stay archive-free.
     */
    includeArchived?: boolean;
};

// How many of the newest comments a line shows before folding the older ones
// behind a "+N mai vechi" hint (the detail dialog has the full thread).
const COMMENTS_SHOWN = 3;

function TicketLine({ ticket, onOpen }: { ticket: Ticket; onOpen: () => void }) {
    const overdue = isOverdue(ticket);
    // Comments arrive only on non-done tickets (withComments listing); guard
    // anyway so a stale cache entry can't put a thread under a closed ticket.
    const comments = ticket.status !== 'done' ? ticket.comments ?? [] : [];
    const shown = comments.slice(-COMMENTS_SHOWN);
    const older = comments.length - shown.length;
    return (
        <button
            type="button"
            onClick={onOpen}
            className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40"
        >
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{ticket.title}</p>
                {ticket.dueDate && (
                    <p
                        className={cn(
                            'mt-0.5 flex items-center gap-1 text-xs',
                            overdue ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400',
                        )}
                    >
                        <CalendarClock aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                        {formatDateRO(ticket.dueDate)}
                        {overdue && ' · depasit'}
                    </p>
                )}
                {shown.length > 0 && (
                    <div className="mt-1.5 space-y-1.5 border-0 border-l-2 border-solid border-gray-200 pl-2 dark:border-gray-600">
                        {older > 0 && (
                            <p className="text-xs italic text-gray-400 dark:text-gray-500">
                                +{pluralRO(older, 'comentariu mai vechi', 'comentarii mai vechi')}
                            </p>
                        )}
                        {shown.map((c) => (
                            <div key={c.id} className="text-xs">
                                <p className="text-gray-400 dark:text-gray-500">
                                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                                        {c.authorName || 'Anonim'}
                                    </span>
                                    {' · '}
                                    {formatDateTimeRO(c.createdAt)}
                                </p>
                                <p className="line-clamp-2 whitespace-pre-wrap break-words text-gray-600 dark:text-gray-300">
                                    {c.body}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {ticket.archivedAt && (
                <span className="shrink-0 text-xs italic text-gray-400 dark:text-gray-500">arhivat</span>
            )}
            <TicketStatusBadge status={ticket.status} />
            <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
        </button>
    );
}

// One object's linked tickets + create-a-linked-ticket. Archived tickets are
// excluded unless `includeArchived` (the order view asks for full history).
// The inner body of both the inline panel and the orders modal.
function EntityTicketsBody({ itemType, itemId, label, includeArchived }: EntityProps) {
    const [creating, setCreating] = useState(false);
    const [viewId, setViewId] = useState<number | null>(null);
    const { data, isLoading } = useListTicketsQuery({
        refType: itemType,
        refId: itemId,
        limit: 50,
        withComments: true,
        includeArchived: includeArchived ? true : undefined,
    });
    const tickets = data?.data ?? [];
    const initialRefs = useMemo<PendingRef[]>(
        () => [{ itemType, itemId: String(itemId), label }],
        [itemType, itemId, label],
    );

    return (
        <div>
            <div className="mb-2 flex items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    <ClipboardList aria-hidden="true" className="h-4 w-4 text-gray-400" />
                    Tickete{!isLoading && ` (${data?.count ?? tickets.length})`}
                </h2>
                <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
                    <Plus aria-hidden="true" className="h-4 w-4" />
                    Ticket nou
                </Button>
            </div>

            {isLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Se incarca...</p>
            ) : tickets.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Niciun ticket legat.</p>
            ) : (
                <ul className="m-0 list-none divide-y divide-gray-100 p-0 dark:divide-gray-700/60">
                    {tickets.map((t) => (
                        <li key={t.id}>
                            <TicketLine ticket={t} onOpen={() => setViewId(t.id)} />
                        </li>
                    ))}
                </ul>
            )}

            <TicketFormDialog open={creating} onOpenChange={setCreating} ticket={null} initialRefs={initialRefs} />
            {/* Ticket opens in place — a dialog over the list, not a redirect
                to /v2/tickets/:id, so the host page (order, dashboard) stays. */}
            <Dialog
                open={viewId !== null}
                onOpenChange={(o) => !o && setViewId(null)}
                maxWidth="max-w-4xl"
                title={viewId !== null ? `Ticket #${viewId}` : 'Ticket'}
            >
                {viewId !== null && <TicketDetailBody ticketId={viewId} onDeleted={() => setViewId(null)} />}
            </Dialog>
        </div>
    );
}

// Inline section card for entity view pages (products, users).
export function EntityTicketsPanel(props: EntityProps) {
    return (
        <section className="rounded-lg border border-solid border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <EntityTicketsBody {...props} />
        </section>
    );
}

// Drop-in replacement for the retired threads ("Probleme") modal contract —
// same {target, onClose} shape, so legacy call sites swap by import alone.
export type TicketsTarget = {
    targetType: TicketReferenceType;
    targetId: number | string;
    label: string;
    /** Full history incl. archived — set by the order views only. */
    includeArchived?: boolean;
};

export function useTicketsModal() {
    const [target, setTarget] = useState<TicketsTarget | null>(null);
    const open = useCallback((next: TicketsTarget) => setTarget(next), []);
    const close = useCallback(() => setTarget(null), []);
    return { target, open, close };
}

export function TicketsModal({ target, onClose }: { target: TicketsTarget | null; onClose: () => void }) {
    return (
        <Dialog
            open={target !== null}
            onOpenChange={(o) => !o && onClose()}
            maxWidth="max-w-2xl"
            title={target ? `Tickete · ${target.label}` : 'Tickete'}
        >
            {target && (
                <EntityTicketsBody
                    itemType={target.targetType}
                    itemId={target.targetId}
                    label={target.label}
                    includeArchived={target.includeArchived}
                />
            )}
        </Dialog>
    );
}

// Header button + modal for pages where tickets aren't a standing section (the
// order view). The button carries the linked-ticket count and turns red while
// any of them is still open — the same signal the old "Probleme" button gave.
export function EntityTicketsModalButton(props: EntityProps) {
    const [open, setOpen] = useState(false);
    // Same args as EntityTicketsBody so the button and the opened modal share
    // one cache entry (one request serves both).
    const { data } = useListTicketsQuery({
        refType: props.itemType,
        refId: props.itemId,
        limit: 50,
        withComments: true,
        includeArchived: props.includeArchived ? true : undefined,
    });
    const count = data?.count ?? 0;
    const hasOpenWork = (data?.data ?? []).some((t) => t.status !== 'done');

    return (
        <>
            <Button variant={hasOpenWork ? 'danger' : 'secondary'} size="sm" onClick={() => setOpen(true)}>
                <ClipboardList aria-hidden="true" className="mr-1 h-4 w-4 shrink-0" />
                Tickete{count > 0 ? ` (${count})` : ''}
            </Button>
            <Dialog open={open} onOpenChange={setOpen} maxWidth="max-w-2xl" title={`Tickete · ${props.label}`}>
                <EntityTicketsBody {...props} />
            </Dialog>
        </>
    );
}
